import React, { useContext, useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import {
  createBudgetLine,
  updateBudgetLine,
} from '../../clients/budget_line_client';
import { getUserSelectList } from '../../clients/user_client';
import { AppContext } from '../../App';
import DropdownSelect from '../Generic/DropdownSelect';
import DropdownMultiselect from '../Generic/DropdownMultiselect';
import { showToast } from '../../config_and_helpers/helpers';
import { logInfo, logError } from '../../config_and_helpers/logger';
import { hydrateBudgetSaveForList } from '../../utils/budgetListRow';

const LOG_CONTEXT = 'BudgetLineFormModal';

/**
 * Resolve PI option from nested primary_investigator or id.
 *
 * @param {Object|null|undefined} line
 * @param {Array<{value:*,label:string}>} userOptions
 * @returns {{value:*,label:string}|null}
 */
function resolvePiOption(line, userOptions) {
  const pi = line?.primary_investigator;
  const piId =
    pi?.id ??
    pi?.user_id ??
    line?.primary_investigator_id ??
    null;
  if (piId == null) return null;
  const fromList = userOptions.find(
    o => String(o.value) === String(piId)
  );
  if (fromList) return fromList;
  const label =
    pi?.first_name || pi?.last_name
      ? `${pi.first_name || ''} ${pi.last_name || ''}`.trim()
      : pi?.username || `User ${piId}`;
  return { value: piId, label };
}

/**
 * Resolve related user options from nested related_users or ids.
 *
 * @param {Object|null|undefined} line
 * @param {Array<{value:*,label:string}>} userOptions
 * @returns {Array<{value:*,label:string}>}
 */
function resolveRelatedOptions(line, userOptions) {
  const nested = Array.isArray(line?.related_users) ? line.related_users : [];
  if (nested.length) {
    return nested.map(u => {
      const id = u.id ?? u.user_id;
      const fromList = userOptions.find(o => String(o.value) === String(id));
      if (fromList) return fromList;
      const label =
        u.first_name || u.last_name
          ? `${u.first_name || ''} ${u.last_name || ''}`.trim()
          : u.username || `User ${id}`;
      return { value: id, label };
    });
  }
  const ids = Array.isArray(line?.related_user_ids) ? line.related_user_ids : [];
  return ids
    .map(id => userOptions.find(o => String(o.value) === String(id)))
    .filter(Boolean);
}

/**
 * Create or edit an institutional Budget (POST/PATCH /budgets/).
 */
const BudgetLineFormModal = ({ show, onHide, token, line, onSaved }) => {
  const { token: contextToken } = useContext(AppContext);
  const authToken = token || contextToken;

  const [number, setNumber] = useState('');
  const [name, setName] = useState('');
  const [allocatedAmount, setAllocatedAmount] = useState('');
  const [currency, setCurrency] = useState('NIS');
  const [isActive, setIsActive] = useState(true);
  const [notes, setNotes] = useState('');
  const [userOptions, setUserOptions] = useState([]);
  const [selectedPi, setSelectedPi] = useState(null);
  const [selectedRelated, setSelectedRelated] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const isEdit = Boolean(line?.id);

  useEffect(() => {
    if (!show || !authToken) return;
    getUserSelectList(authToken, setUserOptions).then(result => {
      if (!result?.success) {
        logError(LOG_CONTEXT, 'getUserSelectList failed', result?.error);
      }
    });
  }, [show, authToken]);

  useEffect(() => {
    if (!show) return;
    const budgetNumber = String(line?.number ?? line?.code ?? '').trim();
    setNumber(budgetNumber);
    setName(line?.name ?? '');
    setAllocatedAmount(line?.allocated_amount ?? '');
    setCurrency(line?.currency ?? 'NIS');
    setIsActive(line?.is_active !== false);
    setNotes(line?.notes ?? '');
    setSelectedPi(resolvePiOption(line, userOptions));
    setSelectedRelated(resolveRelatedOptions(line, userOptions));
  }, [show, line, userOptions]);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!authToken) return;

    const trimmedNumber = number.trim();
    if (!trimmedNumber) {
      showToast('Budget number is required', 'error', 'top-right', 4000);
      return;
    }

    const payload = {
      number: trimmedNumber,
      code: trimmedNumber,
      name: name.trim(),
      allocated_amount: allocatedAmount.trim() || '0',
      currency: currency.trim() || 'NIS',
      is_active: isActive,
      notes: notes.trim() || '',
      primary_investigator_id: selectedPi?.value ?? null,
      related_user_ids: (selectedRelated || []).map(u => u.value),
    };

    setSubmitting(true);
    logInfo(LOG_CONTEXT, 'submit', {
      isEdit,
      budgetId: line?.id ?? null,
      number: trimmedNumber,
      piId: payload.primary_investigator_id,
      relatedCount: payload.related_user_ids.length,
    });

    const result = isEdit
      ? await updateBudgetLine(authToken, line.id, payload)
      : await createBudgetLine(authToken, payload);

    setSubmitting(false);

    if (result.success) {
      result.toast?.();
      const listRow = hydrateBudgetSaveForList(result.data, {
        id: line?.id,
        selectedPi,
        selectedRelated,
      });
      logInfo(LOG_CONTEXT, 'save success', {
        isEdit,
        budgetId: listRow?.id ?? line?.id ?? null,
        piId: listRow?.primary_investigator_id ?? null,
      });
      onSaved?.(listRow);
      onHide();
    } else {
      showToast(
        result.messages?.[0] || 'Failed to save budget',
        'error',
        'top-right',
        5000
      );
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {isEdit
            ? `Edit budget ${
                String(line?.number ?? line?.code ?? '').trim() ||
                line?.name ||
                `#${line.id}`
              }`
            : 'Create budget'}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Budget number</Form.Label>
            <Form.Control
              value={number}
              onChange={e => setNumber(e.target.value)}
              required
              disabled={submitting}
              placeholder="Unique budget / grant number"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Name</Form.Label>
            <Form.Control
              value={name}
              onChange={e => setName(e.target.value)}
              required
              disabled={submitting}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Primary investigator</Form.Label>
            <DropdownSelect
              optionsList={userOptions}
              label="PI"
              selectedValue={selectedPi}
              setSelectedValue={setSelectedPi}
              placeholder="Select primary investigator (optional)"
              disabledOptions={[]}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Related users</Form.Label>
            <DropdownMultiselect
              optionsList={userOptions}
              selectedValues={selectedRelated}
              setSelectedValues={setSelectedRelated}
              placeholder="users"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Allocated amount</Form.Label>
            <Form.Control
              type="text"
              value={allocatedAmount}
              onChange={e => setAllocatedAmount(e.target.value)}
              disabled={submitting}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Currency</Form.Label>
            <Form.Control
              value={currency}
              onChange={e => setCurrency(e.target.value)}
              disabled={submitting}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              disabled={submitting}
            />
          </Form.Group>
          <Form.Check
            type="switch"
            id="budget-is-active"
            label="Active"
            checked={isActive}
            onChange={e => setIsActive(e.target.checked)}
            disabled={submitting}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? (
              <Spinner as="span" animation="border" size="sm" />
            ) : isEdit ? (
              'Save'
            ) : (
              'Create'
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default BudgetLineFormModal;
